<template>
  <div v-if="visible" class="anw-widget">
    <!-- Botão flutuante: SÓ compor um novo recado (some se não há destinatário) -->
    <!-- salvia-778: o `v-if` sobe para o `v-tooltip`. Deixado no botao, o
         componente passaria a renderizar um elemento vazio quando nao ha
         destinatario — e o dock decide o que mostrar contando filhos que
         renderizam. -->
    <v-tooltip v-if="targets.length" left>
      <template v-slot:activator="{ on, attrs }">
        <v-btn
          fab
          color="primary"
          elevation="6"
          class="anw-fab"
          aria-label="Escrever recado"
          v-bind="attrs"
          v-on="on"
          @click="openCompose"
        >
          <v-icon>mdi-message-plus</v-icon>
        </v-btn>
      </template>
      <span>Escrever recado</span>
    </v-tooltip>

    <RecadoComposeDialog
      v-model="composeOpen"
      :targets="targets"
      :is-assistant="isAssistant"
      @sent="onChanged"
    />
    <RecadoListDialog
      v-model="listOpen"
      :notes="notes"
      :current-user-id="userId"
      @refresh="loadInbox"
      @open-thread="openThread"
      @open-compose="openCompose"
    />
    <RecadoThreadDialog
      v-model="threadOpen"
      :note="activeNote"
      :current-user-id="userId"
      @updated="loadInbox"
    />
  </div>
</template>

<script>
import { getComposeTargets, getInbox } from "@/services/assistantNoteService";
import RecadoComposeDialog from "@/components/shared/recados/RecadoComposeDialog.vue";
import RecadoListDialog from "@/components/shared/recados/RecadoListDialog.vue";
import RecadoThreadDialog from "@/components/shared/recados/RecadoThreadDialog.vue";

const POLL_MS = 30000;

export default {
  name: "AssistantNotesWidget",

  components: { RecadoComposeDialog, RecadoListDialog, RecadoThreadDialog },

  data() {
    return {
      composeOpen: false,
      listOpen: false,
      threadOpen: false,
      activeNote: null,
      notes: [],
      targets: [],
      pendingCount: 0,
      pollTimer: null,
    };
  },

  computed: {
    userId() {
      return this.$store.getters["auth/userId"];
    },
    userType() {
      return this.$store.getters["auth/userType"];
    },
    isAssistant() {
      return this.userType === "assistant";
    },
    isDoctor() {
      return this.userType === "doctor_clinic";
    },
    visible() {
      return this.isAssistant || this.isDoctor;
    },
  },

  watch: {
    userType() {
      this.init();
    },
  },

  mounted() {
    this.init();
    // Aberto pelo sino (NotificationBadge) ou pelo item "Recados" do UserMenu.
    this.$root.$on("open-recados", this.abrirRecados);
    // Pedido de recarga (ex.: UserMenu ao abrir) → recarrega e re-emite pendências.
    this.$root.$on("recados-refresh", this.loadInbox);
  },

  beforeDestroy() {
    this.stopPolling();
    this.$root.$off("open-recados", this.abrirRecados);
    this.$root.$off("recados-refresh", this.loadInbox);
  },

  methods: {
    async init() {
      this.stopPolling();
      if (!this.visible) return;
      try {
        this.targets = await getComposeTargets();
      } catch (e) {
        this.targets = [];
      }
      await this.loadInbox();
      this.startPolling();
    },

    async loadInbox() {
      if (!this.visible) return;
      try {
        const res = await getInbox();
        this.notes = res.notes;
        this.pendingCount = res.pendingCount;
        // Fonte única de verdade da contagem: avisa o UserMenu (bolinha do menu).
        this.$root.$emit("recados-pending", this.pendingCount);
      } catch (e) {
        console.error("Erro ao carregar recados:", e);
      }
    },

    /**
     * Ponto de entrada do evento `open-recados`. Duas chamadas, um caminho:
     *
     *  - UserMenu ("Recados") emite SEM payload  -> caixa de entrada;
     *  - o sino emite com `{ noteId }`           -> a conversa, já aberta.
     *
     * salvia-850. Recarrega a caixa antes de procurar porque o recado que
     * gerou a notificação pode ter chegado depois do último poll (30s) — sem
     * isto, clicar num recado recém-chegado cairia no fallback da lista, que é
     * justamente o que esta task veio eliminar.
     *
     * Qualquer imprevisto (recado apagado, resolvido por outro, caixa que
     * falhou ao carregar) cai na lista em vez de não fazer nada: a notificação
     * sempre leva a algum lugar.
     */
    async abrirRecados(payload) {
      const noteId = Number(payload?.noteId) || null;
      if (!noteId) {
        this.openList();
        return;
      }

      await this.loadInbox();

      const note = this.notes.find((n) => Number(n.id) === noteId);
      if (note) this.openThread(note);
      else this.openList();
    },

    openList() {
      this.listOpen = true;
      this.loadInbox();
    },
    openCompose() {
      this.composeOpen = true;
    },
    openThread(note) {
      this.activeNote = note;
      this.threadOpen = true;
    },
    onChanged() {
      this.loadInbox();
    },

    startPolling() {
      this.stopPolling();
      this.pollTimer = setInterval(() => this.loadInbox(), POLL_MS);
    },
    stopPolling() {
      if (this.pollTimer) {
        clearInterval(this.pollTimer);
        this.pollTimer = null;
      }
    },
  },
};
</script>

<style scoped>
.anw-widget {
  /* Nível 0 da coluna de FABs (ver tokens.css --ds-fab-*). */
  position: fixed;
  right: var(--ds-fab-right, 24px);
  bottom: var(--ds-fab-bottom-0, 24px);
  z-index: 90;
}
.anw-fab {
  width: var(--ds-fab-h-0, 56px) !important;
  height: var(--ds-fab-h-0, 56px) !important;
}
</style>
